import React, { useState } from "react";
import {
    PlaydateInvite,
    ResponseType,
    User,
} from "../../../../../../gql/types";
import { DayPartSelection, TimeSelectionRow } from "../../../../../time/types";
import PlaydateInviteGenericResponseView from "../../../../view/PlaydateInviteGenericResponseView";
import PlaydateInviteCurrentResponseView from "../../../../view/PlaydateInviteCurrentResponseView";
import PlaydateInviteRecipientRejectConfirmView from "../../../../view/PlaydateInviteRecipientRejectConfirmView";
import PlaydateInviteRecipientView from "../../../../view/PlaydateInviteRecipientView";
import { useSubmitPlaydateResponse } from "service/playdate/requests/useSubmitResponse";
import { useToastContext } from "contexts/ToastContext";
import { showToast } from "toast/toast";

interface Props {
    user?: User;
    invite: PlaydateInvite;
    onBack: () => void;
    onDataChange: (success: boolean, updatedInvite?: PlaydateInvite) => void;
    baseZIndex?: number;
}

type Stage = "viewInvite" | "viewCurrent" | "editResponse" | "confirmRejection";

const InPlanningRecipientWorkflow: React.FC<Props> = ({
    user,
    invite,
    onBack,
    onDataChange,
    baseZIndex,
}) => {
    const { submitResponse, loading } = useSubmitPlaydateResponse();
    const { triggerToastEvent } = useToastContext();
    const [stage, setStage] = useState<Stage>("viewInvite");

    const [rejectionMessageDraft, setRejectionMessageDraft] = useState("");

    const isEditing = !!invite.viewerResponse;

    const onCompletion = (success: boolean, updatedInvite?: PlaydateInvite) => {
        if (!success) {
            triggerToastEvent();
        }

        if (success) {
            onDataChange(success, updatedInvite);
        }
    }

    const handleSubmitResponse = async (
        type: ResponseType,
        selectedOptions: DayPartSelection[] | TimeSelectionRow[] | null,
        message: string
    ) => {
        submitResponse({
            invite,
            userSelection: selectedOptions === null ? [] : selectedOptions,
            responseType: type,
            message,
            isEditing,
            onCompletion: onCompletion,
        });
    };

    // -------------------------
    // Stage Rendering
    // -------------------------
    if (invite.viewerResponse && stage === "viewCurrent") {
        return (
            <PlaydateInviteCurrentResponseView
                response={invite.viewerResponse}
                onBack={onBack}
                onModify={() => setStage("editResponse")}
                onReject={() => setStage("confirmRejection")}
                baseZIndex={baseZIndex}
            />
        );
    }

    if (stage === "confirmRejection") {
        return (
            <PlaydateInviteRecipientRejectConfirmView
                message={rejectionMessageDraft}
                onBack={() => setStage("editResponse")}
                onBackToDetails={onBack}
                onSubmitCancel={async (finalMessage: string) => {
                    await handleSubmitResponse(ResponseType.Reject, null, finalMessage);
                }}
                baseZIndex={baseZIndex}
                loading={loading}
            />
        );
    }

    if (stage === "editResponse") {
        return (
            <PlaydateInviteGenericResponseView
                invite={invite}
                onBack={invite.viewerResponse && invite.viewerResponse !== null ? () => setStage("viewCurrent") : onBack}
                onBackToDetails={() => setStage("viewCurrent")}
                baseZIndex={baseZIndex}
                loading={loading}
                onSubmitResponse={async (type, selection, message) => {
                    if (type === ResponseType.Reject) {
                        setRejectionMessageDraft(message);
                        setStage("confirmRejection");
                    } else {
                        await handleSubmitResponse(type, selection, message);
                    }
                }}
            />
        );
    }

    return (
        <PlaydateInviteRecipientView
            user={user}
            invite={invite}
            onBack={onBack}
            onContinue={() => {
                if (isEditing) {
                    setStage("viewCurrent");
                    return;
                }
                setStage("editResponse");
            }}
        />);
};

export default InPlanningRecipientWorkflow;
